import Styles from './styles.module.css';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import MakeStore from './2.makeStore/index';
import EditStore from './2.editStore/index';
import StoreImage from './StoreImage';
import EditPlace from './1.edit/1.place/index';
import EditPentagon from './1.edit/2.pentagon/index';
import EditType from './1.edit/3.type/index';
import { FaChevronRight, FaRegCheckCircle } from "react-icons/fa";
import { BsThreeDots } from "react-icons/bs";
import { collection, onSnapshot, query, orderBy, where, getFirestore, deleteDoc, doc, getDocs, updateDoc, arrayRemove } from 'firebase/firestore';
import ErrorMessage from '../../../package/error/index';
import { useAppSelector } from 'src/salon/app/hook';

type RecruitEditStore = {
  documentId: string;
  setStoreImageLocal: React.Dispatch<React.SetStateAction<string>>;
};

const RecruitEditStore: React.FC<RecruitEditStore> = ({ documentId, setStoreImageLocal }) => {

  const db = getFirestore();

  const [ errorMessage, setErrorMessage ] = useState('');

  const company = useAppSelector((state) => state.company.company);

  const [ storeList, setStoreList ] = useState<storeList[]>([]);
  const [ desc, setDesc ] = useState('');
  const [ editInfo, setEditInfo ] = useState<editInfoList>({ id: '', name: '', number: 0, lastTime: 0 });

  const [ saveCount, setSaveCount ] = useState<boolean>(false);

  // 店舗の追加/編集モーダルを閉じたときに一覧を取り直すためのキー。
  // onSnapshot が更新を取りこぼし、別セクションへ移動しないと反映されない
  // ことがあったため、モーダルを閉じるたびに再購読させる。
  const [ refreshKey, setRefreshKey ] = useState(0);

  type ModalType = 'place' | 'pentagon' | 'type' | null;

  const [ activeModal, setActiveModal ] = useState<{
    type: ModalType;
    storeId: string | null;
  }>({ type: null, storeId: null });

  const [ makeStore, setMakeStore ] = useState<boolean>(false);
  const [ banner, setBanner ] = useState<boolean>(false);

  interface storeList {
    id: string,
    name: string,
    number: number,
    image: string,
    lastTime: number,
    statusPlace: boolean,
    statusPentagon: boolean,
    statusType: boolean,
  }

  interface editInfoList {
    id: string,
    name: string,
    number: number,
    lastTime: number,
  }
  
  useEffect(() => {
    if (!company.id) return;

    const queryRef = query(
      collection(db, 'stores'),
      where('companyId', '==', company.id),
      orderBy('number', 'asc')
    );

    const unsub = onSnapshot(queryRef, (snapshot) => {
      const stores = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          number: data.number,
          image: data.image,
          lastTime: data.last_time?.toMillis?.() ?? 0,
          statusPlace: data.status_place,
          statusPentagon: data.status_pentagon,
          statusType: data.status_type,
        };
      });

      setStoreList(stores);

      // 🔥 集約して1回だけ判定
      const hasIncomplete = stores.some(
        s => !s.statusPlace || !s.statusPentagon || !s.statusType
      );
      setSaveCount(hasIncomplete);
    }, (error) => {
      console.error('stores onSnapshot エラー:', error);
    });

    return unsub;
  }, [company.id, refreshKey]);

  // 追加/編集モーダルを閉じた瞬間に一覧を再購読する
  const modalOpenRef = useRef(false);
  const anyModalOpen = makeStore || editInfo.id !== '';
  useEffect(() => {
    if (modalOpenRef.current && !anyModalOpen) {
      setRefreshKey((k) => k + 1);
    }
    modalOpenRef.current = anyModalOpen;
  }, [anyModalOpen]);

  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setDesc('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const deleteStore = async (docId: string, index: number) => {
    if (index === 0) {
      setErrorMessage("1つ目は削除できません。");
      return;
    }

    await deleteStoreAndReorder(company.id, docId);
  };

  const deleteStoreAndReorder = async (companyId: string, docId: string) => {
    await deleteDoc(doc(db, 'stores', docId));

    const snap = await getDocs(
      query(collection(db, 'stores'), where('companyId', '==', companyId), orderBy('number'))
    );

    await Promise.all(
      snap.docs.map((d, i) =>
        updateDoc(doc(db, 'stores', d.id), { number: i + 1 })
      )
    );
  };

  const check = () => {
    setErrorMessage('');
    if (saveCount) {
      setErrorMessage('未入力の項目があります。');
    } else if (storeList.length === 0) {
      setErrorMessage('店舗は1つ以上登録してください。');
    } else {
      save(true);
    }
  }

  const [ uploading, setUploading ] = useState(false);

  const save = async (e: boolean) => {
    setUploading(true);

    const dataToSend = {
      isCompleted: e,
    };

    const db = getFirestore();

    try {
      setBanner(true);
      await updateDoc(doc(db, "recruits", documentId), { store: dataToSend });
      setTimeout(() => { setBanner(false); }, 1500);
    } catch (error) {
      console.error("🔥 保存に失敗:", error);
      setBanner(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {errorMessage !== '' ? ( <ErrorMessage message={errorMessage} /> ) : null}
      {banner && <div className={Styles.banner}>保存しました。</div>}

      <div className={Styles.title_edit}>
        <h4>店舗情報の編集</h4>
        <div className={Styles.title_edit_btn}>
          <button className={Styles.save_box_now} onClick={() => save(false)}>一時保存</button>
          <button className={Styles.save_box_save} onClick={() => check()}>保存</button>
        </div>
      </div>

      {storeList.map((data, index) =>
        <div className={Styles.store} key={data.id}>
          <div className={Styles.store_top}>
            <StoreImage image={data.image} cacheKey={data.lastTime} alt={data.name} placeholderClassName={Styles.store_img_ph} />
            <h4>{data.name}</h4>
            <div style={{flex: '1'}}></div>
            <div className={Styles.store_list_items_right_top_details}>
              <BsThreeDots className={Styles.store_dots} onClick={() => setDesc(data.id)} />
              {desc === data.id ?
                <div ref={menuRef}>
                  <p onClick={() => {setEditInfo({ id: data.id,  name: data.name, number: data.number, lastTime: data.lastTime }); setDesc('');}}>編集する</p>
                  <p onClick={() => deleteStore(data.id, index)}>削除する</p>
                </div>
              : <></> }
            </div>
          </div>
          <div className={Styles.store_bottom}>
            <div onClick={() => setActiveModal({ type: 'place', storeId: data.id })}
              style={data.statusPlace ? { border: '1px solid var(--mw-line)', boxShadow: 'none' } : { border: '1px solid var(--mw-brand)' }}>
              <p style={data.statusPlace ? { color: 'var(--mw-muted)' } : { color: 'var(--mw-brand)' }}>最寄り駅</p>
              {data.statusPlace ?
                <FaRegCheckCircle className={Styles.store_items_icons_red} />
                :
                <FaChevronRight className={Styles.store_items_icons} />
              }
            </div>
            <div onClick={() => setActiveModal({ type: 'pentagon', storeId: data.id })}
              style={data.statusPentagon ? { border: '1px solid var(--mw-line)', boxShadow: 'none' } : { border: '1px solid var(--mw-brand)' }}>
              <p style={data.statusPentagon ? { color: 'var(--mw-muted)' } : { color: 'var(--mw-brand)' }}>六角形</p>
              {data.statusPentagon ?
                <FaRegCheckCircle className={Styles.store_items_icons_red} />
                :
                <FaChevronRight className={Styles.store_items_icons} />
              }
            </div>
            <div onClick={() => setActiveModal({ type: 'type', storeId: data.id })}
              style={data.statusType ? { border: '1px solid var(--mw-line)', boxShadow: 'none' } : { border: '1px solid var(--mw-brand)' }}>
              <p style={data.statusType ? { color: 'var(--mw-muted)' } : { color: 'var(--mw-brand)' }}>8タイプ診断</p>
              {data.statusType ?
                <FaRegCheckCircle className={Styles.store_items_icons_red} />
                :
                <FaChevronRight className={Styles.store_items_icons} />
              }
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setMakeStore(true)} className={Styles.make_btn}>店舗を追加する</button>

      {makeStore && (
        <MakeStore setStoreImageLocal={setStoreImageLocal} companyId={company.id} documentId={documentId} setMakeStore={setMakeStore} length={storeList.length} />
      )}

      {editInfo.id !== '' && (
        <EditStore setEditInfo={setEditInfo} editInfo={editInfo} />
      )}

      {activeModal.type === 'place' && activeModal.storeId !== null && (
        <EditPlace
          editPlace={activeModal.storeId}
          setEditPlace={() => setActiveModal({ type: null, storeId: null })}
        />
      )}

      {activeModal.type === 'pentagon' && activeModal.storeId !== null && (
        <EditPentagon 
          editPentagon={activeModal.storeId}
          setEditPentagon={() => setActiveModal({ type: null, storeId: null })}
        />
      )}

      {activeModal.type === 'type' && activeModal.storeId !== null && (
        <EditType 
          editType={activeModal.storeId}
          setEditType={() => setActiveModal({ type: null, storeId: null })}
        />
      )}

      {uploading && (
        <div className={Styles.lockOverlay}>
          <div className={Styles.spinner} />
          <p>アップロード中...</p>
        </div>
      )}
    </>
  );
}

export default RecruitEditStore;
